import { Box, Button, Card, CardContent, IconButton, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
	createFlashcard,
	getLatestFlashcardId,
	saveFlashcardData,
} from '../../../../services/DashboardApi';
import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft } from 'react-icons/fa';
import SaveIcon from '@mui/icons-material/Save';
import { logger } from '../../../../utils/logger';
import { setFlashcardList } from '../../../../store/flashcardSlice';
import { logUserActivity } from '../../../../services/UsersApi';

const SelfCreateFlashcard = ({
	boxId,
	topicId,
	subtopicId,
	userId,
	notebookId,
	subject,
	subtopicName,
	topicName,
	onFlashcardSaved,
	setShowCreateOptions,
}) => {
	logger.log('Box Id: ', boxId);
	const [cards, setCards] = useState([{ id: 1, term: '', definition: '' }]);
	const [startTime, setStartTime] = useState(null);
	const dispatch = useDispatch();

	const addCard = () => {
		setCards([...cards, { id: cards.length + 1, term: '', definition: '' }]);
	};

	const handleInputChange = (index, field, value) => {
		if (!startTime) {
			setStartTime(new Date().toISOString()); // First edit = start time
		}
		const updatedCards = [...cards];
		updatedCards[index][field] = value;
		setCards(updatedCards);
	};

	const removeCard = (index) => {
		if (cards.length > 1) {
			setCards(cards.filter((_, i) => i !== index));
		}
	};

	const saveFlashcards = async () => {
		if (cards.some((card) => !card.term || !card.definition)) {
			alert('Please fill all fields before saving.');
			return;
		}

		try {
			const latestIdResponse = await getLatestFlashcardId();
			const id = latestIdResponse.data.latestId;
			const flashcardData = cards.map(({ term, definition }) => ({
				question: term,
				answer: definition,
				type: 'Self Created',
			}));
			dispatch(setFlashcardList(flashcardData));

			const newEntry = {
				FLASHCARD_ID: id.toString(),
				BOX_ID: boxId,
				FLASHCARDS_CONTENT: flashcardData,
				NOTEBOOK_ID: String(notebookId),
				SUBJECT: subject,
				SUBTOPIC_ID: subtopicId,
				SUBTOPIC_NAME: subtopicName,
				TOPIC_ID: topicId,
				TOPIC_NAME: topicName,
				USER_ID: userId,
			};
			logger.log('New Entry Inside saveFlashcards for Self Create: ', newEntry);
			await saveFlashcardData(newEntry);

			const endTime = new Date().toISOString();
			const durationSec = Math.round((new Date(endTime) - new Date(startTime)) / 1000);

			await logUserActivity({
				userId,
				eventType: 'KC_SELF_CREATE_FLASHCARD',
				startTime: startTime,
				endTime: endTime,
				durationSec,
				topicId,
				subtopicId,
				// notebookId,
			});
			if (onFlashcardSaved) {
				onFlashcardSaved(flashcardData);
			}
		} catch (error) {
			logger.log('Error saving flashcards:', error);
		}
	};

	const handleBackClick = () => {
		setShowCreateOptions(true);
		//setAICreated(false);
		//setShowFlashcardForm(false);
		//setPendingPrompt(null);
		//setSelectedFiles([]);
	};

	return (
		<Box sx={{ width: '80%', margin: 'auto', mt: { xs: 2, sm: 5 } }}>
			<div
				style={{
					width: '100%',
					textAlign: 'left',
					paddingBottom: '20px',
				}}
			>
				<FaArrowLeft
					style={{ cursor: 'pointer', fontSize: '30px' }}
					onClick={handleBackClick}
				/>
			</div>
			{cards.map((card, index) => (
				<Card
					key={index}
					sx={{
						backgroundColor: '#000F44',
						color: 'white',
						borderRadius: '12px',
						padding: 2,
						mb: 2,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							paddingLeft: '10px',
						}}
					>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 'bold',
								color: 'white',
								textAlign: 'left',
							}}
						>
							{index + 1}
						</Typography>
						<IconButton onClick={() => removeCard(index)} color="error">
							<DeleteIcon />
						</IconButton>
					</Box>
					<hr style={{ border: '1px solid white' }} />
					<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', sm: 'row' },
								justifyContent: 'space-between',
								alignItems: { xs: 'start', sm: 'center' },
								width: '100%',
							}}
						>
							<TextField
								fullWidth
								label="Enter Term"
								variant="standard"
								value={card.term}
								onChange={(e) => handleInputChange(index, 'term', e.target.value)}
								sx={{
									input: { color: 'white' },
									width: { xs: '100%', sm: '48%' },
									label: { color: 'white' },
									'& .MuiInput-underline:before': {
										borderBottomColor: 'white',
									},
									'& .MuiInput-underline:after': {
										borderBottomColor: 'white',
									},
									'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
										borderBottomColor: 'white',
									},
								}}
							/>
							<TextField
								fullWidth
								label="Enter Definition"
								variant="standard"
								value={card.definition}
								onChange={(e) =>
									handleInputChange(index, 'definition', e.target.value)
								}
								sx={{
									input: { color: 'white' },
									label: { color: 'white' },
									width: { xs: '100%', sm: '48%' },
									marginTop: { xs: 2, sm: 0 },
									'& .MuiInput-underline:before': {
										borderBottomColor: 'white',
									},
									'& .MuiInput-underline:after': {
										borderBottomColor: 'white',
									},
									'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
										borderBottomColor: 'white',
									},
									'& label.Mui-focused': {
										color: 'white',
									},
								}}
							/>
						</Box>
					</CardContent>
				</Card>
			))}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
				<Button
					variant="contained"
					sx={{ backgroundColor: 'blue', textTransform: 'none' }}
					onClick={addCard}
				>
					Add more
				</Button>
				<Button
					variant="contained"
					sx={{ backgroundColor: 'blue', textTransform: 'none' }}
					startIcon={<SaveIcon />}
					onClick={saveFlashcards}
				>
					Save
				</Button>
			</Box>
		</Box>
	);
};

export default SelfCreateFlashcard;
